/**
 * Página de Perfil de Usuario
 * Permite ver y editar información del perfil, cambiar contraseña
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-hot-toast";
import { COUNTRY_CODES } from "@/lib/countryCodes";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input,
  Badge,
  AvatarUpload,
} from "@/components/ui";
import PendingConsentsBanner from "@/components/consent/PendingConsentsBanner";
import {
  User,
  Phone,
  Shield,
  Building2,
  Key,
  Save,
  Loader2,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Mail,
  Calendar,
  MapPin,
  Clock,
  Heart,
  FileText,
  Droplets,
  Trash2,
} from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { usePermissions } from "@/hooks/usePermissions";
import EpsSelect from "@/components/forms/EpsSelect";
import {
  useUpdateProfile,
  useChangePassword,
  useUpdateProfilePhoto,
  useDeleteAccount,
} from "@/hooks/useProfile";
import {
  usePerson,
  usePersonByLinkedUser,
  useUpdatePerson,
} from "@/hooks/usePersons";
import { SYSTEM_ROLE_LABELS } from "@/types";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import DocumentsCard from "./components/DocumentsCard";
import MyWardsCard from "./components/MyWardsCard";
import RequestSectionDirectorCard from "./components/RequestSectionDirectorCard";
import WardsPendingConsents from "@/components/consent/WardsPendingConsents";
import { Users as UsersIcon } from "lucide-react";
import { EMERGENCY_CONTACT_RELATIONSHIPS } from "@/lib/constants/relationships";

// Esquemas de validación - iguales a SetupPage

const personSchema = z.object({
  firstName: z.string().min(2, "Mínimo 2 caracteres"),
  lastName: z.string().min(2, "Mínimo 2 caracteres"),
  idDocumentType: z.enum(["CC", "TI", "RC", "CE", "PASAPORTE", "PPT"]),
  idDocumentNumber: z.string().min(5, "Mínimo 5 caracteres"),
  bloodType: z.enum(["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"], {
    errorMap: () => ({ message: "Selecciona el tipo de sangre" }),
  }),
  gender: z.enum(["male", "female"], {
    errorMap: () => ({ message: "Selecciona el género" }),
  }),
  phoneCountryCode: z.string().default("+57"),
  phone: z.string().min(7, "Teléfono requerido"),
  healthInsurance: z.string().optional(),
  address: z.string().min(1, "Dirección requerida"),
  emergencyContactName: z.string().min(1, "Requerido"),
  emergencyContactRelationship: z.string().min(1, "Requerida"),
  emergencyContactCountryCode: z.string().default("+57"),
  emergencyContactPhone: z.string().min(7, "Requerido"),
});

const documentTypeLabels: Record<string, string> = {
  CC: "Cédula de Ciudadanía",
  TI: "Tarjeta de Identidad",
  RC: "Registro Civil",
  CE: "Cédula de Extranjería",
  PASAPORTE: "Pasaporte",
  PPT: "Permiso por Protección Temporal (PPT)",
};

const bloodTypes = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const relationships = [...EMERGENCY_CONTACT_RELATIONSHIPS];

const toTitleCase = (s: string) =>
  s.trim().replace(/(^|\s)\S/g, (c) => c.toUpperCase());

const filterDigits = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.ctrlKey || e.metaKey) return;
  if (/^(Backspace|Delete|Tab|Escape|Enter|Arrow|Home|End)/.test(e.key)) return;
  if (/^\d$/.test(e.key)) return;
  e.preventDefault();
};

const passwordSchema = z
  .object({
    currentPassword: z.string().min(6, "Mínimo 6 caracteres"),
    newPassword: z.string().min(8, "Mínimo 8 caracteres"),
    confirmPassword: z.string().min(8, "Mínimo 8 caracteres"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
  });

type PersonFormData = z.infer<typeof personSchema>;
type PasswordFormData = z.infer<typeof passwordSchema>;

export default function ProfilePage() {
  const { profile, pendingApproval } = useAuthStore();
  const { role, isClubDirector } = usePermissions();
  const updateProfileMutation = useUpdateProfile();
  const changePasswordMutation = useChangePassword();
  const updatePhotoMutation = useUpdateProfilePhoto();
  const updatePersonMutation = useUpdatePerson();
  const deleteAccountMutation = useDeleteAccount();

  // Cargar datos de persona si está vinculada
  const { data: personById } = usePerson(profile?.personId);
  const { data: personByLinkedUser } = usePersonByLinkedUser(
    profile?.id,
    profile?.email,
  );
  const person = personById || personByLinkedUser;

  const [activeTab, setActiveTab] = useState<
    "profile" | "security" | "documentos" | "wards"
  >("profile");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // BUG-003: Eliminar cuenta propia
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteAccountPassword, setDeleteAccountPassword] = useState("");
  const [deleteAccountConfirmText, setDeleteAccountConfirmText] = useState("");
  const [showDeleteAccountPwd, setShowDeleteAccountPwd] = useState(false);
  const DELETE_CONFIRM_PHRASE = "ELIMINAR MI CUENTA";

  // Form para datos de persona
  const {
    register: registerPerson,
    handleSubmit: handleSubmitPerson,
    formState: { errors: personErrors, isDirty: personDirty },
    reset: resetPerson,
    setValue: setPersonValue,
    watch: watchPerson,
  } = useForm<PersonFormData>({
    resolver: zodResolver(personSchema),
    mode: "onTouched",
    defaultValues: {
      firstName: "",
      lastName: "",
      idDocumentType: "CC",
      idDocumentNumber: "",
      bloodType: undefined,
      gender: undefined,
      phoneCountryCode: "+57",
      phone: "",
      healthInsurance: "",
      address: "",
      emergencyContactName: "",
      emergencyContactRelationship: "",
      emergencyContactCountryCode: "+57",
      emergencyContactPhone: "",
    },
  });

  const personDocType = watchPerson("idDocumentType");

  // Poblar el form de persona cuando carguen los datos
  useEffect(() => {
    if (person) {
      // Extraer código de país usando la lista completa (ordenada de más largo a más corto para evitar +1 antes de +1xxx)
      const parsePhone = (raw: string) => {
        if (!raw.startsWith("+")) return { code: "+57", number: raw };
        const sorted = [...COUNTRY_CODES].sort(
          (a, b) => b.code.length - a.code.length,
        );
        const found = sorted.find((c) => raw.startsWith(c.code));
        return found
          ? { code: found.code, number: raw.slice(found.code.length) }
          : { code: "+57", number: raw };
      };
      const { code: phoneCode, number: phoneNumber } = parsePhone(
        person.phone || "",
      );
      const { code: ecCode, number: ecPhone } = parsePhone(
        person.emergencyContact?.phone || "",
      );
      resetPerson({
        firstName: person.firstName || "",
        lastName: person.lastName || "",
        idDocumentType: (person.idDocumentType as any) || "CC",
        idDocumentNumber: person.idDocumentNumber || "",
        bloodType: (person.bloodType as any) || undefined,
        gender: (person.gender as "male" | "female") || "male",
        phoneCountryCode: phoneCode,
        phone: phoneNumber,
        healthInsurance: person.healthInsurance || "",
        address: person.address || "",
        emergencyContactName: person.emergencyContact?.name || "",
        emergencyContactRelationship:
          person.emergencyContact?.relationship || "",
        emergencyContactCountryCode: ecCode,
        emergencyContactPhone: ecPhone,
      });
    }
  }, [person, resetPerson]);

  const onSubmitPerson = async (data: PersonFormData) => {
    if (!person?.id) return;
    const fullPhone = `${data.phoneCountryCode}${data.phone}`;
    const fullEcPhone = `${data.emergencyContactCountryCode}${data.emergencyContactPhone}`;
    const firstName = data.firstName.trim();
    const lastName = data.lastName.trim();
    const fullName = `${firstName} ${lastName}`;
    try {
      await updatePersonMutation.mutateAsync({
        id: person.id,
        data: {
          firstName,
          lastName,
          fullName,
          phone: fullPhone,
          address: data.address.trim(),
          gender: data.gender as any,
          bloodType: data.bloodType as any,
          healthInsurance: data.healthInsurance?.trim() || undefined,
          idDocumentType: data.idDocumentType as any,
          idDocumentNumber: data.idDocumentNumber.trim(),
          emergencyContact: {
            name: data.emergencyContactName.trim(),
            relationship: data.emergencyContactRelationship.trim(),
            phone: fullEcPhone,
          },
        },
      });
      // También actualizar el displayName en users
      await updateProfileMutation.mutateAsync({
        displayName: fullName,
        phone: fullPhone,
      });
      resetPerson(data);
    } catch {
      // toast shown by the hook
    }
  };

  // Form para contraseña
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors },
    reset: resetPassword,
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmitPassword = async (data: PasswordFormData) => {
    try {
      await changePasswordMutation.mutateAsync({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      toast.success("Contraseña cambiada exitosamente");
      resetPassword();
    } catch (error) {
      const authError = error as { code?: string; message: string };
      if (authError?.code === "auth/wrong-password") {
        toast.error("La contraseña actual es incorrecta");
      } else {
        toast.error("Error al cambiar contraseña");
      }
    }
  };

  const getRoleBadgeColor = () => {
    switch (role) {
      case "admin_union":
        return "bg-purple-100 text-purple-800";
      case "admin_association":
        return "bg-blue-100 text-blue-800";
      case "club_director":
        return "bg-green-100 text-green-800";
      case "member":
        return "bg-amber-100 text-amber-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Formatear fecha de nacimiento (UTC-safe: usa componentes UTC para evitar día-1 en UTC-5)
  const formatBirthDate = (date: any) => {
    if (!date) return null;
    try {
      const d = date.toDate ? date.toDate() : new Date(date);
      // Reconstruir con componentes UTC para que UTC-midnight muestre la fecha correcta
      const utcSafe = new Date(
        d.getUTCFullYear(),
        d.getUTCMonth(),
        d.getUTCDate(),
      );
      return format(utcSafe, "d 'de' MMMM, yyyy", { locale: es });
    } catch {
      return null;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Banner no-bloqueante de consentimientos pendientes */}
      <PendingConsentsBanner variant="card" />

      {/* Banner para usuarios con solicitud pendiente */}
      {pendingApproval && (
        <Link
          to="/pending-approval"
          className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800 hover:bg-amber-100 transition-colors"
        >
          <Clock className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span>Tu solicitud está pendiente de aprobación.</span>
          <span className="ml-auto font-medium text-amber-700 whitespace-nowrap">
            ← Volver a tu solicitud
          </span>
        </Link>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
            <User className="w-5 h-5 text-white" />
          </div>
          Mi Perfil
        </h1>
        <p className="text-gray-500 mt-1 ml-[52px]">
          Gestiona tu información personal y seguridad
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab("profile")}
          className={`px-5 py-2.5 font-medium rounded-lg text-sm transition-all duration-300 flex items-center gap-2 ${
            activeTab === "profile"
              ? "bg-white text-primary-600 shadow-md"
              : "text-gray-500 hover:text-gray-700 hover:bg-white/50"
          }`}
        >
          <User className="w-4 h-4" />
          Información
        </button>
        <button
          onClick={() => setActiveTab("security")}
          className={`px-5 py-2.5 font-medium rounded-lg text-sm transition-all duration-300 flex items-center gap-2 ${
            activeTab === "security"
              ? "bg-white text-primary-600 shadow-md"
              : "text-gray-500 hover:text-gray-700 hover:bg-white/50"
          }`}
        >
          <Key className="w-4 h-4" />
          Seguridad
        </button>
        {person && (
          <button
            onClick={() => setActiveTab("documentos")}
            className={`px-5 py-2.5 font-medium rounded-lg text-sm transition-all duration-300 flex items-center gap-2 ${
              activeTab === "documentos"
                ? "bg-white text-primary-600 shadow-md"
                : "text-gray-500 hover:text-gray-700 hover:bg-white/50"
            }`}
          >
            <FileText className="w-4 h-4" />
            Documentos
          </button>
        )}
        <button
          onClick={() => setActiveTab("wards")}
          className={`px-5 py-2.5 font-medium rounded-lg text-sm transition-all duration-300 flex items-center gap-2 ${
            activeTab === "wards"
              ? "bg-white text-primary-600 shadow-md"
              : "text-gray-500 hover:text-gray-700 hover:bg-white/50"
          }`}
        >
          <UsersIcon className="w-4 h-4" />A mi cargo
        </button>
      </div>

      {activeTab === "profile" ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Info de cuenta */}
          <Card className="border-0 shadow-md overflow-hidden">
            <CardContent className="pt-6">
              {/* Avatar con upload */}
              <div className="flex justify-center mb-4">
                <AvatarUpload
                  currentImageUrl={profile?.photoUrl || person?.photoUrl}
                  name={profile?.displayName || ""}
                  entityType="profiles"
                  entityId={profile?.id || ""}
                  onUpload={(url) => updatePhotoMutation.mutate(url)}
                  onRemove={() => updatePhotoMutation.mutate(null)}
                  size="lg"
                />
              </div>

              <div className="text-center mb-4">
                <h3 className="font-bold text-lg text-gray-900">
                  {profile?.displayName}
                </h3>
                <p className="text-gray-500 text-sm flex items-center justify-center gap-1 mt-1">
                  <Mail className="w-3 h-3" />
                  {profile?.email}
                </p>
              </div>

              {/* Rol y estado */}
              <div className="flex justify-center gap-2 mb-4">
                <Badge className={getRoleBadgeColor()}>
                  {role ? SYSTEM_ROLE_LABELS[role] : "Sin rol"}
                </Badge>
                <Badge className="bg-green-100 text-green-800">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Activo
                </Badge>
              </div>

              <div className="pt-4 border-t border-gray-100 space-y-3">
                {/* Información de persona vinculada */}
                {person && (
                  <>
                    {person.birthDate && (
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">
                          {formatBirthDate(person.birthDate)}
                        </span>
                      </div>
                    )}
                    {person.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{person.phone}</span>
                      </div>
                    )}
                    {person.address && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{person.address}</span>
                      </div>
                    )}
                  </>
                )}

                {profile?.scopeName && (
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">{profile.scopeName}</span>
                  </div>
                )}

                {isClubDirector && profile?.directorOfClub && (
                  <div className="pt-2 mt-2 border-t">
                    <span className="text-gray-500 text-xs block mb-2">
                      Director de
                    </span>
                    <Badge className="bg-purple-100 text-purple-800">
                      {profile.directorOfClub.logoUrl ? (
                        <img
                          src={profile.directorOfClub.logoUrl}
                          alt={profile.directorOfClub.name}
                          className="w-4 h-4 rounded mr-1 object-cover"
                        />
                      ) : (
                        <Shield className="w-3 h-3 mr-1" />
                      )}
                      {profile.directorOfClub.name}
                    </Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Columna derecha */}
          <div className="lg:col-span-2 space-y-6">
            {/* F12-P3: autoservicio para pedir la dirección de una sección
                vacante del club donde ya es miembro (antes solo por WhatsApp). */}
            <RequestSectionDirectorCard />

            {/* Card: Datos personales (solo si tiene persona vinculada) */}
            {person ? (
              <Card className="border-0 shadow-md overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-gray-50/50 to-primary-50/30">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center shadow-sm shadow-emerald-500/25">
                      <FileText className="w-4 h-4 text-white" />
                    </div>
                    Datos personales
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-5">
                  <form
                    onSubmit={handleSubmitPerson(onSubmitPerson)}
                    className="space-y-5"
                  >
                    {/* Correo (solo lectura) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        <Mail className="w-4 h-4 inline-block mr-1" />
                        Correo electrónico
                      </label>
                      <Input
                        value={profile?.email || ""}
                        disabled
                        className="bg-gray-50 text-gray-500"
                      />
                      <p className="text-gray-400 text-xs mt-1">
                        El correo no se puede cambiar
                      </p>
                    </div>

                    {/* Nombre legal */}
                    <div className="pt-2 border-t">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
                        Nombre legal
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Nombres *
                          </label>
                          <Input
                            {...registerPerson("firstName")}
                            placeholder="Ej. Juan Carlos"
                            className={
                              personErrors.firstName ? "border-red-500" : ""
                            }
                            onBlur={(e) =>
                              setPersonValue(
                                "firstName",
                                toTitleCase(e.target.value),
                                { shouldDirty: true },
                              )
                            }
                          />
                          {personErrors.firstName && (
                            <p className="text-red-500 text-xs mt-1">
                              {personErrors.firstName.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Apellidos *
                          </label>
                          <Input
                            {...registerPerson("lastName")}
                            placeholder="Ej. Gómez López"
                            className={
                              personErrors.lastName ? "border-red-500" : ""
                            }
                            onBlur={(e) =>
                              setPersonValue(
                                "lastName",
                                toTitleCase(e.target.value),
                                { shouldDirty: true },
                              )
                            }
                          />
                          {personErrors.lastName && (
                            <p className="text-red-500 text-xs mt-1">
                              {personErrors.lastName.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Documento e identidad */}
                    <div className="pt-4 border-t">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
                        Documento de identidad
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Tipo *
                          </label>
                          <select
                            {...registerPerson("idDocumentType")}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                          >
                            {Object.entries(documentTypeLabels).map(
                              ([val, label]) => (
                                <option key={val} value={val}>
                                  {label}
                                </option>
                              ),
                            )}
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Número *
                          </label>
                          <Input
                            {...registerPerson("idDocumentNumber")}
                            placeholder="Número de documento"
                            className={
                              personErrors.idDocumentNumber
                                ? "border-red-500"
                                : ""
                            }
                            onKeyDown={
                              ["CC", "TI", "RC"].includes(personDocType)
                                ? filterDigits
                                : undefined
                            }
                          />
                          {personErrors.idDocumentNumber && (
                            <p className="text-red-500 text-xs mt-1">
                              {personErrors.idDocumentNumber.message}
                            </p>
                          )}
                        </div>
                      </div>
                      {/* Fecha de nacimiento - solo lectura */}
                      {person.birthDate && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            <Calendar className="w-4 h-4 inline-block mr-1" />
                            Fecha de nacimiento
                          </label>
                          <Input
                            value={formatBirthDate(person.birthDate) || ""}
                            disabled
                            className="bg-gray-50 text-gray-500"
                          />
                          <p className="text-gray-400 text-xs mt-1">
                            La fecha de nacimiento no se puede modificar
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Datos físicos y salud */}
                    <div className="pt-4 border-t">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
                        Datos físicos y salud
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Género *
                          </label>
                          <select
                            {...registerPerson("gender")}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                          >
                            <option value="male">Masculino</option>
                            <option value="female">Femenino</option>
                          </select>
                          {personErrors.gender && (
                            <p className="text-red-500 text-xs mt-1">
                              {personErrors.gender.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            <Droplets className="w-4 h-4 inline-block mr-1" />
                            Tipo de sangre *
                          </label>
                          <select
                            {...registerPerson("bloodType")}
                            className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white ${personErrors.bloodType ? "border-red-500" : "border-gray-300"}`}
                          >
                            <option value="">-- Seleccionar --</option>
                            {bloodTypes.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                          {personErrors.bloodType && (
                            <p className="text-red-500 text-xs mt-1">
                              {personErrors.bloodType.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            <Heart className="w-4 h-4 inline-block mr-1" />
                            EPS / Seguro de salud
                          </label>
                          <EpsSelect
                            value={watchPerson("healthInsurance") || ""}
                            onChange={(v) =>
                              setPersonValue("healthInsurance", v, {
                                shouldDirty: true,
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>

                    {/* Contacto */}
                    <div className="pt-4 border-t">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
                        Contacto
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            <Phone className="w-4 h-4 inline-block mr-1" />
                            Teléfono *
                          </label>
                          <div className="flex gap-2">
                            <select
                              {...registerPerson("phoneCountryCode")}
                              className="w-32 rounded-lg border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                            >
                              {COUNTRY_CODES.map((c) => (
                                <option
                                  key={`${c.code}-${c.name}`}
                                  value={c.code}
                                >
                                  {c.flag} {c.code} {c.name}
                                </option>
                              ))}
                            </select>
                            <Input
                              {...registerPerson("phone")}
                              placeholder="300 123 4567"
                              className={`flex-1 ${personErrors.phone ? "border-red-500" : ""}`}
                              onKeyDown={filterDigits}
                            />
                          </div>
                          {personErrors.phone && (
                            <p className="text-red-500 text-xs mt-1">
                              {personErrors.phone.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            <MapPin className="w-4 h-4 inline-block mr-1" />
                            Dirección *
                          </label>
                          <Input
                            {...registerPerson("address")}
                            placeholder="Calle 123 # 45-67"
                            className={
                              personErrors.address ? "border-red-500" : ""
                            }
                            onBlur={(e) =>
                              setPersonValue(
                                "address",
                                toTitleCase(e.target.value),
                                { shouldDirty: true },
                              )
                            }
                          />
                          {personErrors.address && (
                            <p className="text-red-500 text-xs mt-1">
                              {personErrors.address.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Contacto de emergencia */}
                    <div className="pt-4 border-t">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
                        Contacto de emergencia
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Nombre *
                          </label>
                          <Input
                            {...registerPerson("emergencyContactName")}
                            placeholder="Nombre completo"
                            className={
                              personErrors.emergencyContactName
                                ? "border-red-500"
                                : ""
                            }
                            onBlur={(e) =>
                              setPersonValue(
                                "emergencyContactName",
                                toTitleCase(e.target.value),
                                { shouldDirty: true },
                              )
                            }
                          />
                          {personErrors.emergencyContactName && (
                            <p className="text-red-500 text-xs mt-1">
                              {personErrors.emergencyContactName.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Parentesco *
                          </label>
                          <select
                            {...registerPerson("emergencyContactRelationship")}
                            className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white ${personErrors.emergencyContactRelationship ? "border-red-500" : "border-gray-300"}`}
                          >
                            <option value="">-- Seleccionar --</option>
                            {relationships.map((r) => (
                              <option key={r} value={r}>
                                {r}
                              </option>
                            ))}
                          </select>
                          {personErrors.emergencyContactRelationship && (
                            <p className="text-red-500 text-xs mt-1">
                              {
                                personErrors.emergencyContactRelationship
                                  .message
                              }
                            </p>
                          )}
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            <Phone className="w-4 h-4 inline-block mr-1" />
                            Teléfono de emergencia *
                          </label>
                          <div className="flex gap-2">
                            <select
                              {...registerPerson("emergencyContactCountryCode")}
                              className="w-32 rounded-lg border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                            >
                              {COUNTRY_CODES.map((c) => (
                                <option
                                  key={`${c.code}-${c.name}`}
                                  value={c.code}
                                >
                                  {c.flag} {c.code} {c.name}
                                </option>
                              ))}
                            </select>
                            <Input
                              {...registerPerson("emergencyContactPhone")}
                              placeholder="300 123 4567"
                              className={`flex-1 ${personErrors.emergencyContactPhone ? "border-red-500" : ""}`}
                              onKeyDown={filterDigits}
                            />
                          </div>
                          {personErrors.emergencyContactPhone && (
                            <p className="text-red-500 text-xs mt-1">
                              {personErrors.emergencyContactPhone.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end pt-2">
                      <Button
                        type="submit"
                        disabled={
                          !personDirty || updatePersonMutation.isPending
                        }
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        {updatePersonMutation.isPending ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Guardar datos personales
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-0 shadow-md overflow-hidden">
                <CardContent className="pt-8 pb-8 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500">
                    Aún no tienes datos personales registrados en un club.
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Cuando el director te registre como miembro, podrás editar
                    tu información aquí.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      ) : activeTab === "documentos" ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-3">
            {person && <DocumentsCard person={person} />}
          </div>
        </div>
      ) : activeTab === "wards" ? (
        <div className="max-w-2xl space-y-6">
          {/* Acudientes sin membresía propia (pendientes/rechazados) no llegan
              al dashboard: este es su único punto para completar check-ins. */}
          <WardsPendingConsents />
          <MyWardsCard />
        </div>
      ) : (
        <div className="max-w-xl">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-md shadow-purple-500/25">
                  <Key className="w-4 h-4 text-white" />
                </div>
                Cambiar Contraseña
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form
                onSubmit={handleSubmitPassword(onSubmitPassword)}
                className="space-y-5"
              >
                <div className="bg-gradient-to-r from-amber-50 to-amber-100/50 border-2 border-amber-200 rounded-xl p-4 flex items-start gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-sm shadow-amber-500/25 flex-shrink-0">
                    <AlertCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-800">
                      Aviso de seguridad
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      Después de cambiar tu contraseña deberás iniciar sesión
                      nuevamente.
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Contraseña actual *
                  </label>
                  <div className="relative">
                    <Input
                      {...registerPassword("currentPassword")}
                      type={showCurrentPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className={
                        passwordErrors.currentPassword ? "border-red-500" : ""
                      }
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowCurrentPassword(!showCurrentPassword)
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {passwordErrors.currentPassword && (
                    <p className="text-red-500 text-sm mt-1">
                      {passwordErrors.currentPassword.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Nueva contraseña *
                  </label>
                  <div className="relative">
                    <Input
                      {...registerPassword("newPassword")}
                      type={showNewPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className={
                        passwordErrors.newPassword ? "border-red-500" : ""
                      }
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    >
                      {showNewPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {passwordErrors.newPassword && (
                    <p className="text-red-500 text-sm mt-1">
                      {passwordErrors.newPassword.message}
                    </p>
                  )}
                  <p className="text-gray-400 text-xs mt-1">
                    Mínimo 8 caracteres
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Confirmar nueva contraseña *
                  </label>
                  <div className="relative">
                    <Input
                      {...registerPassword("confirmPassword")}
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className={
                        passwordErrors.confirmPassword ? "border-red-500" : ""
                      }
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {passwordErrors.confirmPassword && (
                    <p className="text-red-500 text-sm mt-1">
                      {passwordErrors.confirmPassword.message}
                    </p>
                  )}
                </div>

                <div className="pt-4 border-t">
                  <Button
                    type="submit"
                    disabled={changePasswordMutation.isPending}
                    className="w-full"
                  >
                    {changePasswordMutation.isPending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Key className="w-4 h-4 mr-2" />
                    )}
                    Guardar nueva contraseña
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* BUG-003: Zona de peligro - Eliminar cuenta */}
          <Card className="border-2 border-red-200 shadow-lg mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <div className="w-8 h-8 bg-gradient-to-br from-red-400 to-red-600 rounded-xl flex items-center justify-center shadow-md shadow-red-500/25">
                  <Trash2 className="w-4 h-4 text-white" />
                </div>
                Zona de peligro
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700 mb-4">
                Eliminar tu cuenta es <strong>irreversible</strong>. Tu acceso
                al sistema desaparecerá inmediatamente, tus datos personales
                serán anonimizados y tus membresías quedarán desvinculadas (los
                directores de tus clubes podrán eliminarlas si lo consideran
                necesario).
              </p>
              {profile?.directorOfClub?.id && (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    Eres director del club{" "}
                    <strong>{profile.directorOfClub.name}</strong>. Antes de
                    eliminar tu cuenta debes transferir la dirección o cerrar el
                    club.
                  </div>
                </div>
              )}
              <Button
                variant="danger"
                onClick={() => setShowDeleteAccountModal(true)}
                disabled={!!profile?.directorOfClub?.id}
                className="rounded-xl"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar mi cuenta
              </Button>
            </CardContent>
          </Card>

          {/* Modal confirmación eliminar cuenta */}
          {showDeleteAccountModal && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
                <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-red-500/30">
                  <Trash2 className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
                  Eliminar cuenta permanentemente
                </h3>
                <p className="text-gray-600 mb-4 text-sm">
                  Esta acción es <strong>irreversible</strong>. Para confirmar,
                  escribe{" "}
                  <code className="bg-gray-100 px-1.5 py-0.5 rounded text-red-600 font-mono text-xs">
                    {DELETE_CONFIRM_PHRASE}
                  </code>{" "}
                  e ingresa tu contraseña actual.
                </p>

                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Confirmación
                  </label>
                  <Input
                    type="text"
                    value={deleteAccountConfirmText}
                    onChange={(e) =>
                      setDeleteAccountConfirmText(e.target.value)
                    }
                    placeholder={DELETE_CONFIRM_PHRASE}
                    autoFocus
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Contraseña actual
                  </label>
                  <div className="relative">
                    <Input
                      type={showDeleteAccountPwd ? "text" : "password"}
                      value={deleteAccountPassword}
                      onChange={(e) => setDeleteAccountPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowDeleteAccountPwd((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showDeleteAccountPwd ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex justify-center gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowDeleteAccountModal(false);
                      setDeleteAccountPassword("");
                      setDeleteAccountConfirmText("");
                    }}
                    className="rounded-xl"
                    disabled={deleteAccountMutation.isPending}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() =>
                      deleteAccountMutation.mutate({
                        currentPassword: deleteAccountPassword,
                      })
                    }
                    disabled={
                      deleteAccountMutation.isPending ||
                      deleteAccountConfirmText.trim() !==
                        DELETE_CONFIRM_PHRASE ||
                      deleteAccountPassword.length < 6
                    }
                    className="rounded-xl bg-gradient-to-r from-red-500 to-red-600 shadow-lg shadow-red-500/25"
                  >
                    {deleteAccountMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Eliminando...
                      </>
                    ) : (
                      "Eliminar definitivamente"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
